import React, { useEffect, useState } from 'react';
import { managerAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Building2, DoorOpen, FileText, CreditCard, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ManagerDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const response = await managerAPI.getDashboard();
      setDashboard(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Access Error</h2>
        <p className="text-muted-foreground">{error}</p>
      </div>
    );
  }

  const stats = dashboard?.stats || {};
  const permissions = dashboard?.permissions || {};

  const countPermissions = (perms) => {
    if (!perms) return 0;
    return Object.values(perms).filter(v => v === true).length;
  };

  return (
    <div className="space-y-6" data-testid="manager-dashboard">
      {/* Welcome Banner */}
      <Card className="bg-gradient-to-r from-[#C85A42] to-[#A84832] text-white">
        <CardContent className="p-6">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold mb-1">Property Manager Dashboard</h1>
              <p className="text-white/80">
                Managing properties for {dashboard?.landlord?.name || 'Landlord'}
              </p>
            </div>
            <div className="text-right">
              <Badge className="bg-white/20 text-white border-0">
                {countPermissions(permissions)} Permissions
              </Badge>
              {dashboard?.access_expires_at && (
                <p className="text-xs text-white/70 mt-2">
                  <Clock className="w-3 h-3 inline mr-1" />
                  Access expires: {new Date(dashboard.access_expires_at).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card 
          className={permissions.view_properties ? "cursor-pointer hover:shadow-md transition-shadow" : "opacity-50"}
          onClick={() => permissions.view_properties && navigate('/manager/properties')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Properties</CardTitle>
            <Building2 className="h-4 w-4 text-[#C85A42]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_properties || 0}</div>
            <p className="text-xs text-muted-foreground">
              {dashboard?.all_properties ? 'All properties' : 'Assigned properties'}
            </p>
          </CardContent>
        </Card>

        <Card 
          className={permissions.view_units ? "cursor-pointer hover:shadow-md transition-shadow" : "opacity-50"}
          onClick={() => permissions.view_units && navigate('/manager/units')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Units</CardTitle>
            <DoorOpen className="h-4 w-4 text-[#6B8E6B]" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_units || 0}</div>
            <p className="text-xs text-muted-foreground">
              {stats.occupied_units || 0} occupied
            </p>
          </CardContent>
        </Card>

        <Card 
          className={permissions.view_leases ? "cursor-pointer hover:shadow-md transition-shadow" : "opacity-50"}
          onClick={() => permissions.view_leases && navigate('/manager/leases')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Active Leases</CardTitle>
            <FileText className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.active_leases || 0}</div>
            <p className="text-xs text-muted-foreground">Current tenants</p>
          </CardContent>
        </Card>

        <Card 
          className={permissions.view_payments ? "cursor-pointer hover:shadow-md transition-shadow" : "opacity-50"}
          onClick={() => permissions.view_payments && navigate('/manager/payments')}
        >
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Pending Payments</CardTitle>
            <CreditCard className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">{stats.pending_payments || 0}</div>
            <p className="text-xs text-muted-foreground">
              {permissions.verify_payments ? 'Click to review' : 'View only'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Permissions Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Your Permissions</CardTitle>
          <CardDescription>What you can do as a property manager</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(permissions).map(([key, value]) => (
              <div key={key} className="flex items-center gap-2">
                {value ? (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-muted-foreground" />
                )}
                <span className={`text-sm ${value ? '' : 'text-muted-foreground'}`}>
                  {key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
